/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import Ic from '../Icon';

// ── Password strength ─────────────────────────────────────────────────────────
function passwordStrength(p) {
  if (!p) return { score: 0, label: '', color: '' };
  let score = 0;
  if (p.length >= 8)  score++;
  if (p.length >= 12) score++;
  if (/[A-Z]/.test(p)) score++;
  if (/[0-9]/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  const map = [
    { label: 'Very weak',   color: '#f85149' },
    { label: 'Weak',        color: '#f97316' },
    { label: 'Fair',        color: '#d29922' },
    { label: 'Good',        color: '#4ade80' },
    { label: 'Strong',      color: '#22c55e' },
    { label: 'Very strong', color: '#16a34a' },
  ];
  return { score, ...map[Math.min(score, map.length - 1)] };
}

function StrengthBar({ password }) {
  const { score, label, color } = passwordStrength(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 3,
            background: i <= score ? color : '#2d333b',
            transition: 'background 0.2s',
          }}/>
        ))}
      </div>
      <div style={{ fontSize: 10, color, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

// ── Rate lock helpers ─────────────────────────────────────────────────────────
const LOCK_KEY  = 'rl_login_fail';
const MAX_TRIES = 5;
const LOCK_MS   = 15 * 60 * 1000;

function getLock() {
  try { return JSON.parse(localStorage.getItem(LOCK_KEY) || '{}'); }
  catch { return {}; }
}
function setLock(s) { localStorage.setItem(LOCK_KEY, JSON.stringify(s)); }
function clearLock() { localStorage.removeItem(LOCK_KEY); }

function secsLeft() {
  const { lockedUntil } = getLock();
  if (!lockedUntil) return 0;
  return Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
}

function fmtCountdown(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AuthScreen({ onAuth, onDemo, onShowTerms, onShowPrivacy }) {
  // 'login' | 'signup' | 'forgot' | 'forgot_sent'
  const [mode,      setMode]      = useState('login');
  const [email,     setEmail]     = useState('');
  const [pass,      setPass]      = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [name,      setName]      = useState('');
  const [role,      setRole]      = useState('landlord');
  const [err,       setErr]       = useState('');
  const [loading,   setLoading]   = useState(false);
  const [countdown, setCountdown] = useState(secsLeft);
  const timerRef = useRef(null);

  // Live countdown when locked — always ticks, secsLeft() reads localStorage
  useEffect(() => {
    const id = setInterval(() => {
      const left = secsLeft();
      setCountdown(left);
      if (left <= 0) clearLock();
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const reset = () => { setErr(''); setEmail(''); setPass(''); setConfirm(''); setName(''); };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validatePassword = (p) => {
    if (p.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(p)) return 'Password must contain at least one uppercase letter.';
    if (!/[0-9]/.test(p)) return 'Password must contain at least one number.';
    return null;
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const submit = async () => {
    setErr('');

    if (mode === 'forgot') {
      if (!email) { setErr('Enter your email address.'); return; }
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setLoading(false);
      if (error) { setErr(error.message); return; }
      setMode('forgot_sent');
      return;
    }

    if (!email || !pass) { setErr('Email and password are required.'); return; }

    if (mode === 'signup') {
      if (!name.trim()) { setErr('Full name is required.'); return; }
      const pwErr = validatePassword(pass);
      if (pwErr) { setErr(pwErr); return; }
      if (pass !== confirm) { setErr('Passwords do not match.'); return; }
    }

    // Rate lock check (login only)
    if (mode === 'login') {
      const left = secsLeft();
      if (left > 0) {
        setErr(`Too many failed attempts. Try again in ${fmtCountdown(left)}.`);
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password: pass });
        if (error) {
          // Record failed attempt
          const lock = getLock();
          const attempts = (lock.attempts || 0) + 1;
          if (attempts >= MAX_TRIES) {
            setLock({ attempts, lockedUntil: Date.now() + LOCK_MS });
            setCountdown(Math.ceil(LOCK_MS / 1000));
            setErr(`Too many failed attempts. Locked for 15 minutes.`);
          } else {
            setLock({ attempts });
            const remaining = MAX_TRIES - attempts;
            setErr(`${error.message} (${remaining} attempt${remaining !== 1 ? 's' : ''} remaining)`);
          }
          setLoading(false);
          return;
        }
        clearLock();
        onAuth && onAuth(data.session);
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password: pass,
          options: { data: { full_name: name.trim(), role } },
        });
        if (error) { setErr(error.message); setLoading(false); return; }
        // Create starter subscription row — no DB trigger needed
        if (data.user) {
          await supabase.from('subscriptions').insert({
            user_id: data.user.id,
            tier: 'starter',
            status: 'trialing',
          }).select().single();
        }
        if (data.session) {
          onAuth && onAuth(data.session);
        } else {
          setErr('Account created! Check your email to confirm, then sign in.');
          setLoading(false);
        }
      }
    } catch(e) { setErr(e.message); setLoading(false); }
  };

  const isLocked = countdown > 0;

  const s = {
    wrap:  { minHeight: '100vh', background: '#0e1117', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Outfit',sans-serif" },
    box:   { background: '#161b22', border: '1px solid #21262d', borderRadius: 16, padding: 36, width: '100%', maxWidth: 420 },
    logo:  { textAlign: 'center', marginBottom: 28 },
    logoT: { fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 900, color: '#fff' },
    title: { fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 800, color: '#fff', textAlign: 'center', marginBottom: 6 },
    sub:   { fontSize: 13, color: '#8b949e', textAlign: 'center', marginBottom: 24 },
    label: { fontSize: 12, fontWeight: 600, color: '#8b949e', display: 'block', marginBottom: 5 },
    input: { width: '100%', background: '#21262d', border: '1px solid #2d333b', borderRadius: 8, padding: '11px 13px', color: '#f5f0e8', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: "'Outfit',sans-serif" },
    btn:   { width: '100%', padding: 13, borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#16a34a,#4ade80)', color: '#0a1a0a', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" },
    err:   { background: 'rgba(248,81,73,.08)', border: '1px solid rgba(248,81,73,.2)', borderRadius: 8, padding: '10px 13px', fontSize: 13, color: '#f85149', marginBottom: 16 },
    ok:    { background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 8, padding: '10px 13px', fontSize: 13, color: '#4ade80', marginBottom: 16 },
    lock:  { background: 'rgba(249,115,22,.08)', border: '1px solid rgba(249,115,22,.25)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 },
    link:  { color: '#4ade80', cursor: 'pointer', fontWeight: 600 },
    bottom:{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#8b949e' },
    fi:    { marginBottom: 16 },
    roles: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 },
    roleB: (active) => ({ padding: '10px', borderRadius: 8, border: '1px solid ' + (active ? 'rgba(34,197,94,.4)' : '#2d333b'), background: active ? 'rgba(34,197,94,.08)' : 'transparent', color: active ? '#4ade80' : '#8b949e', cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'center', fontFamily: "'Outfit',sans-serif" }),
  };

  // ── Forgot-password sent confirmation ────────────────────────────────────────
  if (mode === 'forgot_sent') {
    return (
      <div style={s.wrap}>
        <div style={s.box}>
          <div style={{ textAlign: 'center', padding: '10px 0 20px' }}>
            <div style={{ width: 56, height: 56, background: 'rgba(34,197,94,.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Ic n="send" s={24} c="#4ade80"/>
            </div>
            <div style={s.title}>Check your email</div>
            <div style={{ ...s.sub, marginTop: 8 }}>
              We sent a password reset link to<br/>
              <strong style={{ color: '#f5f0e8' }}>{email}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#484f58', marginTop: 12 }}>
              Didn't receive it? Check your spam folder or{' '}
              <span style={s.link} onClick={() => { setMode('forgot'); setErr(''); }}>try again</span>.
            </div>
          </div>
          <button style={s.btn} onClick={() => { reset(); setMode('login'); }}>Back to Sign In</button>
        </div>
      </div>
    );
  }

  // ── Forgot-password form ─────────────────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <div style={s.wrap}>
        <div style={s.box}>
          <div style={s.logo}>
            <div style={s.logoT}>Rent<span style={{ color: '#22c55e' }}>Lord</span></div>
          </div>
          <div style={s.title}>Reset your password</div>
          <div style={{ ...s.sub, marginTop: 6 }}>Enter your email and we'll send a reset link.</div>

          {err && <div style={s.err}>{err}</div>}

          <div style={s.fi}>
            <label style={s.label}>Email Address</label>
            <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@email.com" autoFocus onKeyDown={e => e.key === 'Enter' && submit()}/>
          </div>

          <button style={s.btn} onClick={submit} disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
          <div style={s.bottom}>
            <span style={s.link} onClick={() => { reset(); setMode('login'); }}>← Back to Sign In</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Login / Signup ───────────────────────────────────────────────────────────
  const isOk = err.startsWith('Account created') || err.startsWith('Check your email');

  return (
    <div style={s.wrap}>
      <div style={s.box}>
        <div style={s.logo}>
          <svg width="36" height="36" viewBox="0 0 64 64" fill="none" style={{ marginBottom: 8, display: 'block', margin: '0 auto 8px' }}>
            <path d="M10 48 L16 24 L28 36 L32 16 L36 36 L48 24 L54 48 Z" fill="#22c55e"/>
            <rect x="10" y="50" width="44" height="6" rx="3" fill="#22c55e"/>
            <circle cx="32" cy="13" r="4" fill="#86efac"/>
          </svg>
          <div style={s.logoT}>Rent<span style={{ color: '#22c55e' }}>Lord</span></div>
        </div>

        <div style={s.title}>{mode === 'login' ? 'Welcome back' : 'Create your account'}</div>
        <div style={s.sub}>{mode === 'login' ? 'Sign in to your account' : 'Start your 14-day free trial'}</div>

        {/* Rate lock banner */}
        {isLocked && mode === 'login' && (
          <div style={s.lock}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#f97316', marginBottom: 4 }}>Account temporarily locked</div>
            <div style={{ fontSize: 12, color: '#8b949e' }}>
              Too many failed attempts. Try again in{' '}
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#f97316' }}>{fmtCountdown(countdown)}</span>
            </div>
          </div>
        )}

        {err && !isLocked && <div style={isOk ? s.ok : s.err}>{err}</div>}

        {mode === 'signup' && (
          <div style={s.fi}>
            <label style={s.label}>Full Name</label>
            <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="First and last name" autoFocus/>
          </div>
        )}

        <div style={s.fi}>
          <label style={s.label}>Email Address</label>
          <input style={{ ...s.input, opacity: isLocked ? 0.5 : 1 }} type="email" value={email}
            onChange={e => setEmail(e.target.value)} placeholder="you@email.com" disabled={isLocked}/>
        </div>

        <div style={s.fi}>
          <label style={s.label}>Password</label>
          <input
            style={{ ...s.input, opacity: isLocked ? 0.5 : 1 }}
            type="password"
            value={pass}
            onChange={e => setPass(e.target.value)}
            placeholder={mode === 'signup' ? 'Min 8 chars, 1 uppercase, 1 number' : 'Your password'}
            onKeyDown={e => e.key === 'Enter' && mode === 'login' && submit()}
            disabled={isLocked}
          />
          {mode === 'signup' && <StrengthBar password={pass}/>}
        </div>

        {mode === 'signup' && (
          <div style={s.fi}>
            <label style={s.label}>Confirm Password</label>
            <input
              style={{ ...s.input, borderColor: confirm && pass !== confirm ? '#f85149' : '#2d333b' }}
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat your password"
              onKeyDown={e => e.key === 'Enter' && submit()}
            />
            {confirm && pass !== confirm && (
              <div style={{ fontSize: 11, color: '#f85149', marginTop: 4 }}>Passwords do not match</div>
            )}
          </div>
        )}

        {mode === 'signup' && (
          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>I AM A</label>
            <div style={s.roles}>
              <button style={s.roleB(role === 'landlord')} onClick={() => setRole('landlord')}>🏠 Landlord / Owner</button>
              <button style={s.roleB(role === 'tenant')}   onClick={() => setRole('tenant')}>👤 Tenant</button>
            </div>
          </div>
        )}

        <button style={{ ...s.btn, opacity: isLocked ? 0.5 : 1, cursor: isLocked ? 'not-allowed' : 'pointer' }}
          onClick={submit} disabled={loading || isLocked}>
          {loading ? 'Please wait...' : isLocked ? `Locked — ${fmtCountdown(countdown)}` : (mode === 'login' ? 'Sign In' : 'Create Account')}
        </button>

        {mode === 'login' && (
          <div style={{ textAlign: 'right', marginTop: 10 }}>
            <span style={{ ...s.link, fontSize: 12 }} onClick={() => { reset(); setMode('forgot'); }}>
              Forgot password?
            </span>
          </div>
        )}

        {mode === 'signup' && (
          <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: '#484f58', lineHeight: 1.6 }}>
            By creating an account you agree to our{' '}
            <span style={{ ...s.link, fontSize: 11 }} onClick={onShowTerms}>Terms of Service</span>
            {' '}and{' '}
            <span style={{ ...s.link, fontSize: 11 }} onClick={onShowPrivacy}>Privacy Policy</span>
          </div>
        )}

        <div style={s.bottom}>
          {mode === 'login'
            ? <>Don't have an account? <span style={s.link} onClick={() => { reset(); setMode('signup'); }}>Sign up free</span></>
            : <>Already have an account? <span style={s.link} onClick={() => { reset(); setMode('login'); }}>Sign in</span></>
          }
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #21262d' }}>
          <span style={{ fontSize: 12, color: '#484f58' }}>Just exploring? </span>
          <span style={s.link} onClick={onDemo}>View demo with sample data →</span>
        </div>
      </div>
    </div>
  );
}
